import { useId, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import type { PayrollTaxLiabilityReport } from "@/api/types";
import "./ReportsPayrollTaxLiability.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** 30 days back from today, UTC — same rolling-window default
 *  `ReportsVendorPurchases.tsx`'s own `defaultRangeStart` uses, duplicated on purpose
 *  (that file's own reasoning: each report's own default is free to diverge later). */
function defaultRangeStart(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

function defaultRangeEnd(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Two decimal places, always, no currency symbol — the money face this report's own
 *  figures use everywhere: the on-screen table and the downloaded CSV both render the
 *  same plain number (`PayrollTaxLiabilityCsv`'s own `M` helper, server-side). A "not a
 *  filing" summary is read alongside the farm's own deposit worksheet, not a bank
 *  statement — a bare figure, not a currency-styled one, is what that reading calls for. */
function money(amount: number): string {
  return amount.toFixed(2);
}

/**
 * Payroll tax liability summary — Reports' worksheet of the farm's own FICA/withholding
 * deposit obligation, per finalized pay run, for a window the owner chooses. ef-054-5,
 * ADR 0009 §6 ("Reports is one picker" — a report renders inside the template at
 * `/reports/{slug}`, no area tab strip of its own; "All reports" below is the only way
 * back).
 *
 * <p>
 * NOT A FILING — the disclaimer below is ALWAYS ON SCREEN, never gated behind the report
 * or behind `Why`, same "visible whether or not a report has been requested yet" posture
 * `ReportsScheduleF.tsx`'s own disclaimer takes: a worksheet a farmer might screenshot or
 * print needs "not a tax filing" visible immediately.
 * </p>
 *
 * <p>
 * NO ENTITY PICKER — same tenant-wide shape `ReportsVendorPurchases.tsx` takes.
 * `PayrollTaxLiabilityReportEndpoints` rolls up every finalized, non-voided pay run on the
 * tenant regardless of entity (`PayrollTaxLiabilityReportRepository`'s own remarks).
 * </p>
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Apply IS PRESSED — same "no guessed window" discipline
 * `ReportsVendorPurchases.tsx`'s own remarks give: a liability figure read against a
 * window nobody asked for is a number a farmer did not request. The From/To inputs still
 * carry a sensible rolling-30-day default so the form is never two empty fields.
 * </p>
 */
export function ReportsPayrollTaxLiability() {
  const fromId = useId();
  const toId = useId();

  const [from, setFrom] = useState(defaultRangeStart);
  const [to, setTo] = useState(defaultRangeEnd);

  const [report, setReport] = useState<PayrollTaxLiabilityReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const rangeIsBackwards = from !== "" && to !== "" && to < from;
  const canSubmit = from !== "" && to !== "" && !rangeIsBackwards && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getPayrollTaxLiabilityReport({ from, to });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the payroll tax liability summary."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the report,
   * same reasoning `ReportsVendorPurchases.tsx`'s identical download gives: paying for the
   * whole CSV body on every date change would be wasted work for the visits that never
   * press the button.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getPayrollTaxLiabilityCsv({ from, to });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline ReportsVendorPurchases.tsx's identical
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-payroll-tax-liability-${from}-to-${to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the payroll tax liability file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device AreaTabs.tsx's own handleNavClick already uses rather
    // than threading a `navigate` prop through every screen this job touches: pushState
    // doesn't fire popstate on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];
  const isEmpty = status === "ready" && rows.length === 0;

  return (
    <main className="payroll-tax-liability">
      <PageHeader
        title="Payroll tax liability summary"
        subtitle="FICA and withholding owed, summed over finalized pay runs"
      />

      <a href="/reports" className="payroll-tax-liability__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <p className="payroll-tax-liability__disclaimer" role="note">
        <strong>Not a tax filing.</strong> This is a deposit-liability summary built from
        this farm&rsquo;s own finalized pay runs — it is not a 941, a 943, or any other
        completed, filed return.
      </p>

      <section
        className="payroll-tax-liability__panel"
        aria-labelledby="payroll-tax-liability-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="payroll-tax-liability-heading" className="payroll-tax-liability__heading">
          Summary
        </h2>

        <form className="payroll-tax-liability__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="payroll-tax-liability__field">
            <label htmlFor={fromId}>From</label>
            <input
              id={fromId}
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>
          <div className="payroll-tax-liability__field">
            <label htmlFor={toId}>To</label>
            <input
              id={toId}
              type="date"
              value={to}
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary payroll-tax-liability__apply" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Apply"}
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="payroll-tax-liability__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        <Why>
          <strong>FICA Employee</strong> and each withholding column are the farm&rsquo;s
          own amounts owed on money already withheld from a paycheck —{" "}
          <strong>FICA Employer</strong> is the employer&rsquo;s matching share of the same
          tax. A draft or voided run never appears; only a finalized, non-voided pay run
          whose period ended inside your window counts.
        </Why>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the payroll tax liability summary."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing to show for this window."
            detail="No finalized pay run's period ended inside this window."
          />
        )}

        {status === "ready" && rows.length > 0 && report && (
          <>
            <div
              className="payroll-tax-liability__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="payroll-tax-liability-heading"
            >
              <table className="payroll-tax-liability__table ruled-rows">
                <caption className="payroll-tax-liability__caption">
                  {rows.length} {rows.length === 1 ? "pay run" : "pay runs"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Pay Period End</th>
                    <th scope="col" className="payroll-tax-liability__num">FICA Employee</th>
                    <th scope="col" className="payroll-tax-liability__num">FICA Employer</th>
                    <th scope="col" className="payroll-tax-liability__num">Federal WH</th>
                    <th scope="col" className="payroll-tax-liability__num">State WH</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.payRunId}>
                      <th scope="row" className="payroll-tax-liability__period">{row.payPeriodEnd}</th>
                      <td className="payroll-tax-liability__num payroll-tax-liability__amount">
                        {money(row.ficaEmployee)}
                      </td>
                      <td className="payroll-tax-liability__num payroll-tax-liability__amount">
                        {money(row.ficaEmployer)}
                      </td>
                      <td className="payroll-tax-liability__num payroll-tax-liability__amount">
                        {money(row.federalWh)}
                      </td>
                      <td className="payroll-tax-liability__num payroll-tax-liability__amount">
                        {money(row.stateWh)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Total</th>
                    <td className="payroll-tax-liability__num payroll-tax-liability__amount">
                      {money(report.totalFicaEmployee)}
                    </td>
                    <td className="payroll-tax-liability__num payroll-tax-liability__amount">
                      {money(report.totalFicaEmployer)}
                    </td>
                    <td className="payroll-tax-liability__num payroll-tax-liability__amount">
                      {money(report.totalFederalWh)}
                    </td>
                    <td className="payroll-tax-liability__num payroll-tax-liability__amount">
                      {money(report.totalStateWh)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="payroll-tax-liability__export">
              <h3 className="payroll-tax-liability__export-title">For your records</h3>
              <p className="payroll-tax-liability__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary payroll-tax-liability__export-btn"
                disabled={csvState.kind === "busy" || !canSubmit}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="payroll-tax-liability__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="payroll-tax-liability__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
